import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ServiceAccess from './components/ServiceAccess';
import { Toaster } from "sonner";
import { HidScannerProvider } from "./contexts/HidScannerContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PortalProvider } from "./contexts/PortalContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import PortalEntry from "./pages/PortalEntry";
const ResetRequest = lazy(() => import("./pages/ResetRequest"));
const ICaardMoney = lazy(() => import("./pages/ICaardMoney"));
import PortalSelect from "./pages/PortalSelect";
import SeoManager from "./lib/seo";
import Layout from "./components/Layout";
import InstallPrompt from "./components/InstallPrompt";
import OfflineBanner from "./components/OfflineBanner";
import { bootManifestFromStorage } from "./lib/manifest";
const Landing = lazy(() => import("./pages/Landing"));
import Auth from "./pages/Auth";
const LegacyActivateRedirect = lazy(() => import("./pages/LegacyActivateRedirect"));
const ScanSuccess = lazy(() => import("./pages/ScanSuccess"));
const QRRegistry = lazy(() => import("./pages/QRRegistry"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const Ads = lazy(() => import("./pages/Ads"));
const Staff = lazy(() => import("./pages/Staff"));
import Support from "./pages/Support";
import Dashboard from "./pages/Dashboard";
const Wallet = lazy(() => import("./pages/Wallet"));
const MyCard = lazy(() => import("./pages/MyCard"));
const CardHistory = lazy(() => import("./pages/CardHistory"));
const AdminNfcStudio = lazy(() => import("./pages/AdminNfcStudio"));
const PhysicalCardGate = lazy(() => import("./pages/PhysicalCardGate"));
const MerchantSellICaard = lazy(() => import("./pages/MerchantSellICaard"));
const AdminCardSystem = lazy(() => import("./pages/AdminCardSystem"));
const UniversalCardGate = lazy(() => import("./pages/UniversalCardGate"));
const UniqueCardGate = lazy(() => import("./pages/UniqueCardGate"));
const CustomerJoin = lazy(() => import('./pages/CustomerJoin'));
const CustomerCardRouter = lazy(() => import('./pages/CustomerJoin').then(m=>({default:m.CustomerCardRouter})));
const ActivityCards = lazy(() => import('./pages/ActivityCards'));
const JoinTransport = lazy(() => import("./pages/JoinTransport"));
const Scan = lazy(() => import("./pages/Scan"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Tickets = lazy(() => import("./pages/Tickets"));
const Loyalty = lazy(() => import("./pages/Loyalty"));
const Donations = lazy(() => import("./pages/Donations"));
const Profile = lazy(() => import("./pages/Profile"));
const Merchant = lazy(() => import("./pages/Merchant"));
const Organizer = lazy(() => import("./pages/Organizer"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminRolesManager = lazy(() => import("./pages/AdminRolesManager"));
const SchoolConsole = lazy(() => import("./pages/SchoolConsole"));
const MySchool = lazy(() => import("./pages/MySchool"));
const JoinSchool = lazy(() => import("./pages/JoinSchool"));
const SchoolStaffConsole = lazy(() => import("./pages/SchoolStaffConsole"));
const JoinSchoolStaff = lazy(() => import("./pages/JoinSchoolStaff"));
const CheckInScanner = lazy(() => import("./pages/CheckInScanner"));
const ScanHistory = lazy(() => import("./pages/ScanHistory"));
const Spin = lazy(() => import("./pages/Spin"));
const SpinAdmin = lazy(() => import("./pages/SpinAdmin"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Settings = lazy(() => import("./pages/Settings"));
const RefundCenter = lazy(() => import("./pages/RefundCenter"));
const AdminRefunds = lazy(() => import("./pages/AdminRefunds"));
const ShopsAdmin = lazy(() => import("./pages/ShopsAdmin"));
const PartnersAdmin = lazy(() => import("./pages/PartnersAdmin"));
const PartnersDirectory = lazy(() => import("./pages/PartnersDirectory"));
const VendorOrderReceipt = lazy(() => import("./pages/VendorOrderReceipt"));
const HostelChat = lazy(() => import("./pages/HostelChat"));
const ServiceProviders = lazy(() => import("./pages/ServiceProviders"));
const MyWorkOrders = lazy(() => import("./pages/MyWorkOrders"));
const ServiceProviderQueue = lazy(() => import("./pages/ServiceProviderQueue"));
const ServiceProviderReceipts = lazy(() => import("./pages/ServiceProviderReceipts"));
const AdminServiceProviders = lazy(() => import("./pages/AdminServiceProviders"));
const AdminShopNetwork = lazy(() => import("./pages/AdminShopNetwork"));
const AdminBanners = lazy(() => import("./pages/AdminBanners"));
const AdminCodeLibrary = lazy(() => import("./pages/AdminCodeLibrary"));
const HostelOwnerWorkOrders = lazy(() => import("./pages/HostelOwnerWorkOrders"));
const CampaignsAdmin = lazy(() => import("./pages/CampaignsAdmin"));
const FraudAdmin = lazy(() => import("./pages/FraudAdmin"));
const AdminPlatformEarnings = lazy(() => import("./pages/AdminPlatformEarnings"));
const AdminCommissionPolicy = lazy(() => import("./pages/AdminCommissionPolicy"));
const Partners = lazy(() => import("./pages/Partners"));
const MerchantRegister = lazy(() => import("./pages/MerchantRegister"));
const RecurringDonations = lazy(() => import("./pages/RecurringDonations"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const PublicShops = lazy(() => import("./pages/PublicShops"));
const MerchantSales = lazy(() => import("./pages/MerchantSales"));
const MerchantReplaceCard = lazy(() => import("./pages/MerchantReplaceCard"));
const MerchantFloat = lazy(() => import("./pages/MerchantFloat"));
const MerchantRecharge = lazy(() => import("./pages/MerchantRecharge"));
const MerchantAllocationRequests = lazy(() => import("./pages/MerchantAllocationRequests"));
const MerchantLocation = lazy(() => import("./pages/MerchantLocation"));
const ProfileSecurity = lazy(() => import("./pages/ProfileSecurity"));
const FirebaseCheck = lazy(() => import("./pages/FirebaseCheck"));
const AdminCmsMedia = lazy(() => import("./pages/AdminCmsMedia"));
const AdminCmsSections = lazy(() => import("./pages/AdminCmsSections"));
const AdminAllocationRequests = lazy(() => import("./pages/AdminAllocationRequests"));
const CoordinatorPortal = lazy(() => import("./pages/CoordinatorPortal"));
const EventShare = lazy(() => import("./pages/EventShare"));
const CoordinatorDashboard = lazy(() => import("./pages/CoordinatorDashboard"));
const MyCoordinator = lazy(() => import("./pages/MyCoordinator"));
const CardReceipt = lazy(() => import("./pages/CardReceipt"));
const SmartQueues = lazy(() => import("./pages/SmartQueues"));
const QueueJoinPage = lazy(() => import("./pages/SmartQueues").then((m) => ({ default: m.QueueJoin })));
const MyQueues = lazy(() => import("./pages/MyQueues"));
const CompanyQueues = lazy(() => import("./pages/CompanyQueues"));
const CompanyPromos = lazy(() => import("./pages/CompanyPromos"));
const CompanyFinance = lazy(() => import("./pages/CompanyFinance"));
const CompanyConsole = lazy(() => import("./pages/CompanyConsole"));
const HRConsole = lazy(() => import("./pages/HRConsole"));
const HRLiveQR = lazy(() => import("./pages/HRLiveQR"));
const HRRegister = lazy(() => import("./pages/HRRegister"));
const MyWork = lazy(() => import("./pages/MyWork"));
const DutyRoster = lazy(() => import("./pages/DutyRoster"));
const MyAvailability = lazy(() => import("./pages/MyAvailability"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const GiftClaim = lazy(() => import("./pages/GiftClaim"));
const GiftOperator = lazy(() => import("./pages/GiftOperator"));
const AdminGiftCards = lazy(() => import("./pages/AdminGiftCards"));
const AdminUserSecurity = lazy(() => import("./pages/AdminUserSecurity"));
const AdminCompanies = lazy(() => import("./pages/AdminCompanies"));
const AdminFinance = lazy(() => import("./pages/AdminFinance"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const ManagerRegister = lazy(() => import("./pages/ManagerRegister"));
const ManagerConsole = lazy(() => import("./pages/ManagerConsole"));
const AdminManagers = lazy(() => import("./pages/AdminManagers"));
const AdminHR = lazy(() => import("./pages/AdminHR"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const Guardian = lazy(() => import("./pages/Guardian"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const PromoShop = lazy(() => import("./pages/MyOrders").then((m) => ({ default: m.PromoShop })));
const OrderReceipt = lazy(() => import("./pages/OrderReceipt"));
const AdminCoordinators = lazy(() => import("./pages/AdminCoordinators"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Notifications = lazy(() => import("./pages/Notifications"));
const OrganizerStaff = lazy(() => import("./pages/OrganizerStaff"));
const StudentRegister = lazy(() => import("./pages/StudentRegister"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const AdminStudents = lazy(() => import("./pages/AdminStudents"));
// Legacy per-card QR admin pages retired June 2026 — universal-QR model.
// Their routes now redirect to /app/admin/card-system.
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminDriverReviews = lazy(() => import("./pages/AdminDriverReviews"));
const IDeliveryRider = lazy(() => import("./pages/IDeliveryRider"));
const Connect = lazy(() => import("./pages/Connect"));
const ProfileView = lazy(() => import("./pages/ProfileView"));
const ConductorJoin = lazy(() => import("./pages/ConductorJoin"));
const UniversalScan = lazy(() => import("./pages/UniversalScan"));
const Payouts = lazy(() => import("./pages/Payouts"));
const AdminPayouts = lazy(() => import("./pages/AdminPayouts"));
const AdminUnblockRequests = lazy(() => import("./pages/AdminUnblockRequests"));
const AdminProfileUpdates = lazy(() => import("./pages/AdminProfileUpdates"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const AdminScanLogs = lazy(() => import("./pages/AdminScanLogs"));
const AdminTraceSearch = lazy(() => import("./pages/AdminTraceSearch"));
const AdminWalletAudit = lazy(() => import("./pages/AdminWalletAudit"));
const HostelDetail = lazy(() => import("./pages/HostelDetail"));
const HostelBooking = lazy(() => import("./pages/HostelBooking"));
const HostelTenancy = lazy(() => import("./pages/HostelTenancy"));
const HostelOwnerDashboard = lazy(() => import("./pages/HostelOwnerDashboard"));
const MyTenancies = lazy(() => import("./pages/MyTenancies"));
const LegalCenter = lazy(() => import("./pages/LegalCenter"));
const VerifyDoc = lazy(() => import("./pages/VerifyDoc"));
const AuthPin = lazy(() => import("./pages/AuthPin"));
const AdminKiosks = lazy(() => import("./pages/AdminKiosks"));
import { VendorsList, VendorDetail } from "./pages/VendorsPages";
const VendorOwner = lazy(() => import("./pages/VendorOwner"));
const VendorOrders = lazy(() => import("./pages/VendorOrders"));
const MyVendorOrders = lazy(() => import("./pages/MyVendorOrders"));
const Kiosk = lazy(() => import("./pages/Kiosk"));
const KioskHome = lazy(() => import("./pages/KioskHome"));
import KioskSessionGuard from "./components/KioskSessionGuard";
const ServicesDirectory = lazy(() => import("./pages/ServicesDirectory"));
import Splash from "./components/Splash";
import { ThemeProvider } from "./contexts/ThemeContext";
const ServiceProviderProfile = lazy(() => import("./pages/ServiceProviderProfile"));
const IDelivery = lazy(() => import("./pages/IDelivery"));
const DeliveryProvider = lazy(() => import("./pages/DeliveryProvider"));
const AdminIDelivery = lazy(() => import("./pages/AdminIDelivery"));
const AdminLoyalty = lazy(() => import("./pages/AdminLoyalty"));
const FindMyBed = lazy(() => import("./pages/FindMyBed"));
const HostelPass = lazy(() => import("./pages/HostelPass"));
const PrintHub = lazy(() => import("./pages/PrintHub"));
const TransportHub = lazy(() => import("./pages/TransportHub"));
const TransportBoardPage = lazy(() => import("./pages/TransportBoard"));
const TransportCompany = lazy(() => import("./pages/TransportCompany"));
const TransportBoarding = lazy(() => import("./pages/TransportBoarding"));
const AdminTransport = lazy(() => import("./pages/AdminTransport"));
const PrintShopStudio = lazy(() => import("./pages/PrintShopStudio"));
import CancellationAlertModal from "./components/CancellationAlertModal";
import useUserLocationCapture from "./hooks/useUserLocationCapture";

// One-time location capture for authenticated users (auto-dispatch by GPS).
function LocationCapture() {
  useUserLocationCapture();
  return null;
}
const History = lazy(() => import("./pages/History"));
const Workers = lazy(() => import("./pages/Workers"));
const ConductorHistory = lazy(() => import("./pages/ConductorHistory"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#666]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return <Layout>{children}</Layout>;
}

function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#666]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

const ADMIN_ONLY = ["admin"];
const ADMIN_OR_SUPPORT = ["admin", "support"];

// Role → home dashboard. Used by PublicOnly so a fresh login lands on the
// right console even if the page-level nav() loses the redirect race.
const ROLE_HOME = {
  food_vendor: "/app/vendor/owner",
  hostel_owner: "/app/hostel/owner",
  iprint_shop: "/app/printhub/studio",
  organizer: "/app/organizer",
  service_provider: "/app/service/provider",
  delivery_provider: "/app/idelivery/provider",
  rider: "/app/idelivery/rider",
  merchant: "/app/merchant",
  transport_owner: "/app/transport/company",
  school_admin: "/app/school",
  area_coordinator: "/app/coordinator",
  icompany: "/app/company/console",
  driver: "/app/transport/boarding",
  admin: "/app/admin",
  conductor: "/app/transport/boarding",
  staff: "/app/check-in",
};

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user) {
    // Round 117 — honour ?next= / stashed gift-claim redirects (internal paths only).
    // useLocation (not window.location) so the value stays consistent with the
    // route being rendered even mid-navigation; icaard_next is cleared on a
    // delay so the redirect survives the setUser re-render race.
    const next = new URLSearchParams(location.search).get("next") || localStorage.getItem("icaard_next") || "";
    if (next.startsWith("/") && !next.startsWith("//")) {
      setTimeout(() => localStorage.removeItem("icaard_next"), 3000);
      return <Navigate to={next} replace />;
    }
    return <Navigate to={ROLE_HOME[user.role] || "/app"} replace />;
  }
  return children;
}

/**
 * Smart landing — when an unauthenticated user lands on "/", forward them to
 * their previously-chosen portal so they don't see the selector twice.
 * Authenticated users go straight to /app.
 */
function SmartLanding() {
  // The bare root URL (e.g. https://icaard.com/) ALWAYS shows the public
  // startup page (Portal Selector) on both mobile & desktop — we do NOT
  // auto-forward to the saved portal here. That keeps the home URL
  // welcoming and shareable. If the user wants to skip the selector they
  // can deep-link to `/customer`, `/student`, `/food`, etc. directly.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  useEffect(() => { bootManifestFromStorage(); }, []);
  return (
    <AuthProvider>
      <PortalProvider>
      <FeatureFlagProvider>
      <ThemeProvider>
      <BrowserRouter>
        <HidScannerProvider>
        <SeoManager />
        <Toaster position="top-center" richColors offset={{ top: "calc(env(safe-area-inset-top) + 72px)", left: 12, right: 12 }} mobileOffset={{ top: "calc(env(safe-area-inset-top) + 72px)", left: 12, right: 12 }} toastOptions={{ style: { maxWidth: "calc(100vw - 24px)" } }} />
        <OfflineBanner />
        <InstallPrompt />
        <KioskSessionGuard />
        <Splash />
        <CancellationAlertModal />
        <LocationCapture />
        <Suspense fallback={<div className="min-h-screen bg-[#09090F] flex items-center justify-center text-sm text-[#9CA3D4]">Loading…</div>}>
        <ServiceAccess><Routes>
          {/* Smart landing — forwards to remembered portal OR to /login selector */}
          <Route path="/" element={<SmartLanding />} />
          <Route path="/login" element={<PublicOnly><PortalSelect /></PublicOnly>} />
          <Route path="/customer" element={<PortalEntry />} />
          <Route path="/student" element={<PortalEntry />} />
          <Route path="/organizer" element={<PortalEntry />} />
          <Route path="/merchant" element={<PortalEntry />} />
          <Route path="/driver" element={<PortalEntry />} />
          <Route path="/hostel" element={<PortalEntry />} />
          <Route path="/food" element={<PortalEntry />} />
          <Route path="/iprint" element={<PortalEntry />} />
          <Route path="/service" element={<PortalEntry />} />
          <Route path="/staff" element={<PortalEntry />} />
          <Route path="/conductor" element={<PortalEntry />} />
          <Route path="/auth/reset" element={<ResetRequest />} />
          <Route path="/hr/live" element={<HRLiveQR />} />
          <Route path="/hr/register" element={<HRRegister />} />
          <Route path="/landing" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
          <Route path="/join" element={<CustomerJoin/>}/>
          <Route path="/app/activity-cards" element={<RequireRole roles={['admin','organizer','service_provider','food_vendor','hostel_owner','iprint_shop','delivery_provider','transport_owner','business_owner','institution_manager','icompany']}><ActivityCards/></RequireRole>}/>
          <Route path="/register-merchant" element={<PublicOnly><MerchantRegister /></PublicOnly>} />
          <Route path="/staff-login" element={<Navigate to="/auth" replace />} />
          <Route path="/admin/icaard" element={<PublicOnly><StaffLogin /></PublicOnly>} />
          <Route path="/ad/ic" element={<PublicOnly><AdminPortal /></PublicOnly>} />
          <Route path="/manager/register" element={<ManagerRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/activate" element={<LegacyActivateRedirect />} />
          <Route path="/staff-activate" element={<LegacyActivateRedirect />} />
          <Route path="/transport/driver-activate" element={<LegacyActivateRedirect />} />
          <Route path="/iprint/activate" element={<LegacyActivateRedirect />} />
          <Route path="/conductor-join" element={<ConductorJoin />} />
          <Route path="/s/:serial" element={<CustomerCardRouter param="serial" legacy={<UniversalScan/>}/>} />
          <Route path="/card" element={<UniversalCardGate />} />
          <Route path="/coordinator" element={<CoordinatorPortal />} />
          <Route path="/e/:id" element={<EventShare />} />
          <Route path="/coordinator/register" element={<CoordinatorPortal />} />
          <Route path="/c/:token" element={<CustomerCardRouter param="token" legacy={<UniqueCardGate/>}/>} />
          <Route path="/join-transport" element={<JoinTransport />} />
          <Route path="/card/:cardId" element={<CustomerCardRouter param="cardId" legacy={<PhysicalCardGate/>}/>} />
          <Route path="/student-activate" element={<LegacyActivateRedirect />} />
          <Route path="/shops" element={<PublicShops />} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/wallet" element={<Protected><Wallet /></Protected>} />
          <Route path="/app/money" element={<Protected><ICaardMoney /></Protected>} />
          <Route path="/app/gift-cards" element={<Protected><GiftCards /></Protected>} />
          <Route path="/app/gift-operator" element={<Protected><GiftOperator /></Protected>} />
          <Route path="/app/admin/gift-cards" element={<Protected><AdminGiftCards /></Protected>} />
          <Route path="/app/admin/user-security" element={<Protected><AdminUserSecurity /></Protected>} />
          <Route path="/gift/:token" element={<GiftClaim />} />
          <Route path="/app/money/assistant" element={<Navigate to="/app/money" replace />} />
          <Route path="/app/card" element={<Protected><MyCard /></Protected>} />
          <Route path="/app/card/history" element={<Protected><CardHistory /></Protected>} />
          <Route path="/app/scan" element={<Protected><Scan /></Protected>} />
          <Route path="/app/scan/success" element={<Protected><ScanSuccess /></Protected>} />
          <Route path="/app/check-in" element={<Protected><CheckInScanner /></Protected>} />
          <Route path="/app/scan-history" element={<Protected><ScanHistory /></Protected>} />
          <Route path="/app/spin" element={<Protected><Spin /></Protected>} />
          <Route path="/app/refunds/tickets" element={<Protected><Refunds /></Protected>} />
          <Route path="/app/admin/spin" element={<RequireRole roles={ADMIN_ONLY}><SpinAdmin /></RequireRole>} />
          <Route path="/app/admin/nfc-studio" element={<RequireRole roles={ADMIN_ONLY}><AdminNfcStudio /></RequireRole>} />
          <Route path="/app/admin/card-system" element={<RequireRole roles={ADMIN_ONLY}><AdminCardSystem /></RequireRole>} />
          <Route path="/app/merchant/sell-icaard" element={<Protected><MerchantSellICaard /></Protected>} />
          <Route path="/app/services" element={<Protected><ServiceProviders /></Protected>} />
          <Route path="/app/service-providers" element={<Protected><ServiceProviders /></Protected>} />
          <Route path="/app/events" element={<Protected><Events /></Protected>} />
          <Route path="/app/events/:id" element={<Protected><EventDetail /></Protected>} />
          <Route path="/app/tickets" element={<Protected><Tickets /></Protected>} />
          <Route path="/app/loyalty" element={<Protected><Loyalty /></Protected>} />
          <Route path="/app/donations" element={<Protected><Donations /></Protected>} />
          <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/app/transactions" element={<Protected><Transactions /></Protected>} />
          <Route path="/app/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/app/merchant" element={<Protected><Merchant /></Protected>} />
          <Route path="/app/merchant/sales" element={<Protected><MerchantSales /></Protected>} />
          <Route path="/app/merchant/replace-card" element={<Protected><MerchantReplaceCard /></Protected>} />
          <Route path="/app/merchant/float" element={<Protected><MerchantFloat /></Protected>} />
          <Route path="/app/merchant/recharge" element={<Protected><MerchantRecharge /></Protected>} />
          <Route path="/app/merchant/allocation-requests" element={<Protected><MerchantAllocationRequests /></Protected>} />
          <Route path="/app/merchant/location" element={<Protected><MerchantLocation /></Protected>} />
          <Route path="/app/profile/security" element={<Protected><ProfileSecurity /></Protected>} />
          <Route path="/app/dev/firebase-check" element={<Protected><FirebaseCheck /></Protected>} />
          <Route path="/app/admin/cms/media" element={<RequireRole roles={["admin"]}><AdminCmsMedia /></RequireRole>} />
          <Route path="/app/admin/cms/sections" element={<RequireRole roles={["admin"]}><AdminCmsSections /></RequireRole>} />
          <Route path="/app/admin/allocation-requests" element={<RequireRole roles={["admin"]}><AdminAllocationRequests /></RequireRole>} />
          <Route path="/app/organizer" element={<RequireRole roles={["organizer", "admin"]}><Organizer /></RequireRole>} />
          <Route path="/app/organizer/staff" element={<RequireRole roles={["organizer", "admin"]}><OrganizerStaff /></RequireRole>} />
          <Route path="/app/transport/conductor" element={<Navigate to="/app/transport/boarding" replace />} />
          <Route path="/app/transport/driver" element={<Navigate to="/app/transport/boarding" replace />} />
          {/* Legacy iPrint → merged into iPrintHub marketplace */}
          <Route path="/app/iprint" element={<Navigate to="/app/printhub" replace />} />
          <Route path="/app/iprint/send" element={<Navigate to="/app/printhub" replace />} />
          <Route path="/app/iprint/shop" element={<Navigate to="/app/printhub/studio" replace />} />
          <Route path="/app/connect" element={<Protected><Connect /></Protected>} />
          <Route path="/u/:dcn" element={<Protected><ProfileView /></Protected>} />
          <Route path="/app/transport/book" element={<Navigate to="/app/transport" replace />} />
          <Route path="/transport-board" element={<TransportBoardPage />} />
          <Route path="/app/student/register" element={<Protected><StudentRegister /></Protected>} />
          <Route path="/app/student/me" element={<Protected><StudentDashboard /></Protected>} />
          <Route path="/app/admin/students" element={<RequireRole roles={ADMIN_ONLY}><AdminStudents /></RequireRole>} />
          <Route path="/app/admin/permanent-qrs" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/print-cards" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/notifications" element={<RequireRole roles={ADMIN_ONLY}><AdminNotifications /></RequireRole>} />
          <Route path="/app/admin/driver-reviews" element={<RequireRole roles={ADMIN_ONLY}><AdminDriverReviews /></RequireRole>} />
          <Route path="/app/user-management" element={<RequireRole roles={["admin", "support", "manager"]}><UserManagement /></RequireRole>} />
          <Route path="/app/admin" element={<RequireRole roles={ADMIN_ONLY}><Admin /></RequireRole>} />
          <Route path="/app/admin/roles" element={<RequireRole roles={ADMIN_ONLY}><AdminRolesManager /></RequireRole>} />
          <Route path="/app/school" element={<Protected><SchoolConsole /></Protected>} />
          <Route path="/app/my-school" element={<Protected><MySchool /></Protected>} />
          <Route path="/app/join-school" element={<Protected><JoinSchool /></Protected>} />
          <Route path="/app/school-staff" element={<Protected><SchoolStaffConsole /></Protected>} />
          <Route path="/app/join-school-staff/:token" element={<JoinSchoolStaff />} />
          <Route path="/app/admin/universal-qr" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/serial-pools" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/ads" element={<RequireRole roles={ADMIN_ONLY}><Ads /></RequireRole>} />
          <Route path="/app/admin/staff" element={<RequireRole roles={ADMIN_ONLY}><Staff /></RequireRole>} />
          <Route path="/app/admin/settings" element={<RequireRole roles={ADMIN_ONLY}><Settings /></RequireRole>} />
          <Route path="/app/refunds" element={<Protected><RefundCenter /></Protected>} />
          <Route path="/app/admin/refunds" element={<RequireRole roles={ADMIN_ONLY}><AdminRefunds /></RequireRole>} />
          <Route path="/app/admin/shops" element={<RequireRole roles={ADMIN_ONLY}><ShopsAdmin /></RequireRole>} />
          <Route path="/app/admin/partners" element={<RequireRole roles={ADMIN_ONLY}><PartnersAdmin /></RequireRole>} />
          <Route path="/app/directory" element={<Protected><PartnersDirectory /></Protected>} />
          <Route path="/app/receipt/vendor-order/:orderId" element={<Protected><VendorOrderReceipt /></Protected>} />
          {/* Round 48 — P2 features (hostel chat · service providers · code library) */}
          <Route path="/app/hostel/:hostelId/chat" element={<Protected><HostelChat /></Protected>} />
          <Route path="/app/my-work-orders" element={<Protected><MyWorkOrders /></Protected>} />
          <Route path="/app/service/provider" element={<Protected><ServiceProviderQueue /></Protected>} />
          <Route path="/app/service/provider/profile" element={<Protected><ServiceProviderProfile /></Protected>} />
          <Route path="/app/service/provider/receipts" element={<Protected><ServiceProviderReceipts /></Protected>} />
          <Route path="/app/admin/service-providers" element={<RequireRole roles={ADMIN_ONLY}><AdminServiceProviders /></RequireRole>} />
          <Route path="/app/admin/shop-network" element={<RequireRole roles={ADMIN_ONLY}><AdminShopNetwork /></RequireRole>} />
          <Route path="/app/admin/banners" element={<RequireRole roles={ADMIN_ONLY}><AdminBanners /></RequireRole>} />
          <Route path="/app/admin/code-library" element={<RequireRole roles={ADMIN_ONLY}><AdminCodeLibrary /></RequireRole>} />
          <Route path="/app/hostel/work-orders" element={<Protected><HostelOwnerWorkOrders /></Protected>} />
          <Route path="/app/admin/campaigns" element={<RequireRole roles={ADMIN_ONLY}><CampaignsAdmin /></RequireRole>} />
          <Route path="/app/admin/fraud" element={<RequireRole roles={ADMIN_ONLY}><FraudAdmin /></RequireRole>} />
          <Route path="/app/donations/recurring" element={<Protected><RecurringDonations /></Protected>} />
          <Route path="/app/payouts" element={<RequireRole roles={["organizer", "merchant", "iprint_shop", "business_owner", "institution_manager", "agent", "admin", "service_provider", "food_vendor", "hostel_owner", "delivery_provider", "transport_owner", "rider", "school_admin", "area_coordinator", "icompany"]}><Payouts /></RequireRole>} />
          <Route path="/app/coordinator" element={<RequireRole roles={["area_coordinator", "admin"]}><CoordinatorDashboard /></RequireRole>} />
          <Route path="/app/my-coordinator" element={<Protected><MyCoordinator /></Protected>} />
          <Route path="/app/card-receipt/:txnId" element={<Protected><CardReceipt /></Protected>} />
          <Route path="/app/smart-queues" element={<Protected><SmartQueues /></Protected>} />
          <Route path="/app/q/:queueId" element={<Protected><QueueJoinPage /></Protected>} />
          <Route path="/app/my-queues" element={<Protected><MyQueues /></Protected>} />
          <Route path="/app/company/console" element={<RequireRole roles={["icompany", "admin"]}><CompanyConsole /></RequireRole>} />
          <Route path="/app/company/queues" element={<RequireRole roles={["icompany", "admin"]}><CompanyQueues /></RequireRole>} />
          <Route path="/app/company/promotions" element={<RequireRole roles={["icompany", "admin"]}><CompanyPromos /></RequireRole>} />
          <Route path="/app/company/finance" element={<RequireRole roles={["icompany", "admin"]}><CompanyFinance /></RequireRole>} />
          <Route path="/app/hr" element={<Protected><HRConsole /></Protected>} />
          <Route path="/app/work" element={<Protected><MyWork /></Protected>} />
          <Route path="/app/hr/duty" element={<Protected><DutyRoster /></Protected>} />
          <Route path="/app/work/availability" element={<Protected><MyAvailability /></Protected>} />
          <Route path="/app/admin/companies" element={<RequireRole roles={["admin"]}><AdminCompanies /></RequireRole>} />
          <Route path="/app/admin/finance" element={<RequireRole roles={["admin"]}><AdminFinance /></RequireRole>} />
          <Route path="/app/manager" element={<RequireRole roles={["manager", "admin"]}><ManagerConsole /></RequireRole>} />
          <Route path="/app/admin/managers" element={<RequireRole roles={["admin"]}><AdminManagers /></RequireRole>} />
          <Route path="/app/admin/hr" element={<RequireRole roles={["admin"]}><AdminHR /></RequireRole>} />
          <Route path="/app/admin/subscriptions" element={<RequireRole roles={["admin"]}><AdminSubscriptions /></RequireRole>} />
          <Route path="/app/guardian" element={<Protected><Guardian /></Protected>} />
          <Route path="/app/my-orders" element={<Protected><MyOrders /></Protected>} />
          <Route path="/app/promo/:promoId" element={<Protected><PromoShop /></Protected>} />
          <Route path="/app/order-receipt/:orderId" element={<Protected><OrderReceipt /></Protected>} />
          <Route path="/app/admin/coordinators" element={<RequireRole roles={["admin"]}><AdminCoordinators /></RequireRole>} />
          {/* iDelivery — standalone platform delivery service */}
          <Route path="/app/idelivery" element={<Protected><IDelivery /></Protected>} />
          <Route path="/app/idelivery/provider" element={<Protected><DeliveryProvider /></Protected>} />
          <Route path="/app/idelivery/rider" element={<RequireRole roles={["rider", "delivery_provider", "admin"]}><IDeliveryRider /></RequireRole>} />
          <Route path="/app/admin/idelivery" element={<RequireRole roles={ADMIN_ONLY}><AdminIDelivery /></RequireRole>} />
          <Route path="/app/admin/loyalty" element={<RequireRole roles={ADMIN_ONLY}><AdminLoyalty /></RequireRole>} />
          {/* SmartHostel — discovery + digital pass */}
          <Route path="/app/find-my-bed" element={<Protected><FindMyBed /></Protected>} />
          <Route path="/app/hostel-pass/:bookingId" element={<Protected><HostelPass /></Protected>} />
          {/* iPrintHub Marketplace — wizard + shop studio */}
          <Route path="/app/printhub" element={<Protected><PrintHub /></Protected>} />
          <Route path="/app/printhub/studio" element={<RequireRole roles={["iprint_shop", "admin"]}><PrintShopStudio /></RequireRole>} />
          {/* Transport Engine — companies, tickets, boarding-scan settlement */}
          <Route path="/app/transport" element={<Protected><TransportHub /></Protected>} />
          <Route path="/app/transport/company" element={<Protected><TransportCompany /></Protected>} />
          <Route path="/app/transport/boarding" element={<RequireRole roles={["driver", "conductor", "transport_owner", "admin"]}><TransportBoarding /></RequireRole>} />
          <Route path="/app/admin/transport" element={<RequireRole roles={["admin"]}><AdminTransport /></RequireRole>} />
          <Route path="/app/admin/payouts" element={<RequireRole roles={ADMIN_ONLY}><AdminPayouts /></RequireRole>} />
          <Route path="/app/admin/platform-earnings" element={<RequireRole roles={ADMIN_ONLY}><AdminPlatformEarnings /></RequireRole>} />
          <Route path="/app/admin/commission-policy" element={<RequireRole roles={ADMIN_ONLY}><AdminCommissionPolicy /></RequireRole>} />
          <Route path="/app/partners" element={<Protected><Partners /></Protected>} />
          <Route path="/app/admin/unblock-requests" element={<RequireRole roles={ADMIN_ONLY}><AdminUnblockRequests /></RequireRole>} />
          <Route path="/app/admin/profile-updates" element={<RequireRole roles={ADMIN_ONLY}><AdminProfileUpdates /></RequireRole>} />
          <Route path="/app/admin/audit-logs" element={<RequireRole roles={ADMIN_ONLY}><AdminAuditLogs /></RequireRole>} />
          <Route path="/app/admin/scan-logs" element={<RequireRole roles={ADMIN_ONLY}><AdminScanLogs /></RequireRole>} />
          <Route path="/app/admin/activation-qrs" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/cards-record-room" element={<Navigate to="/app/admin/card-system" replace />} />
          <Route path="/app/admin/wallet-audit" element={<RequireRole roles={ADMIN_ONLY}><AdminWalletAudit /></RequireRole>} />
          <Route path="/app/admin/kiosks" element={<RequireRole roles={ADMIN_ONLY}><AdminKiosks /></RequireRole>} />

          {/* Mega push · Vendors + Kiosk + Legal */}
          <Route path="/kiosk" element={<Kiosk />} />
          <Route path="/app/kiosk" element={<Kiosk />} />
          <Route path="/app/kiosk-home" element={<Protected><KioskHome /></Protected>} />
          <Route path="/legal" element={<LegalCenter />} />
          <Route path="/verify/doc/:token" element={<VerifyDoc />} />
          <Route path="/auth/vendor-register" element={<LegacyActivateRedirect />} />
          <Route path="/auth/hostel-register" element={<LegacyActivateRedirect />} />
          <Route path="/auth/service-provider-register" element={<LegacyActivateRedirect />} />
          <Route path="/auth/pin" element={<PublicOnly><AuthPin /></PublicOnly>} />
          <Route path="/app/vendors" element={<Protected><VendorsList /></Protected>} />
          <Route path="/app/vendors/:id" element={<Protected><VendorDetail /></Protected>} />
          <Route path="/app/vendor/owner" element={<RequireRole roles={["food_vendor", "admin"]}><VendorOwner /></RequireRole>} />
          <Route path="/app/vendor/orders" element={<RequireRole roles={["food_vendor", "admin"]}><VendorOrders /></RequireRole>} />
          <Route path="/app/my-vendor-orders" element={<Protected><MyVendorOrders /></Protected>} />

          {/* iHostelHub — discovery front door (legacy /app/hostels browse merged in) */}
          <Route path="/app/hostels" element={<Navigate to="/app/find-my-bed" replace />} />
          <Route path="/app/hostels/:id" element={<Protected><HostelDetail /></Protected>} />
          <Route path="/app/hostels/:hostelId/book/:basketId" element={<Protected><HostelBooking /></Protected>} />
          <Route path="/app/hostels/tenancy/:bookingId" element={<Protected><HostelTenancy /></Protected>} />
          <Route path="/app/hostel/owner" element={<Protected><HostelOwnerDashboard /></Protected>} />
          <Route path="/app/my-tenancies" element={<Protected><MyTenancies /></Protected>} />
          <Route path="/app/admin/trace" element={<RequireRole roles={ADMIN_OR_SUPPORT}><AdminTraceSearch /></RequireRole>} />
          <Route path="/app/workers" element={<RequireRole roles={["driver", "organizer", "admin"]}><Workers /></RequireRole>} />
          <Route path="/app/conductor/checkin" element={<Navigate to="/app/transport/boarding" replace />} />
          <Route path="/app/conductor/history" element={<RequireRole roles={["conductor", "driver", "admin"]}><ConductorHistory /></RequireRole>} />
          <Route path="/app/history" element={<Protected><History /></Protected>} />
          <Route path="/app/support" element={<Protected><Support /></Protected>} />
          <Route path="/app/all-services" element={<Protected><ServicesDirectory /></Protected>} />
          <Route path="/app/account/change-password" element={<Protected><ChangePassword /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes></ServiceAccess>
        </Suspense>
        </HidScannerProvider>
      </BrowserRouter>
      </ThemeProvider>
      </FeatureFlagProvider>
      </PortalProvider>
    </AuthProvider>
  );
}
